<script setup lang="ts">
import { computed } from 'vue';
import store from '@/store';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

defineProps<{
  isPublished?: boolean;
  stickerId?: string | null;
}>();

const item = computed({
  get: () => store.getters['sticker/GET_STICKER'],
  set: (value) => {
    store.dispatch('sticker/SET_STICKER', value);
  },
});
</script>

<template lang="pug">
section.c-tab-messages
  .c-message-card
    .c-message-card__header
      .c-message-card__badge 1
      .c-message-card__header-text
        h4.c-message-card__title Confirmation Message
        p.c-message-card__subtitle This message will be sent to attendees immediately after they purchase a ticket

    .c-message-card__body
      .c-message-card__field-header
        fai(type="fal" icon="envelope").c-message-card__icon
        span Email to attendees upon purchase
      QuillEditor(
        :toolbar="['bold', 'italic', 'underline', 'strike']"
        :placeholder="'Thank you for purchasing a ticket! We\\'re excited to see you at [Event Name] on [Date]. Your ticket details are attached...'"
        v-model:content="item.confirmationEmail"
        content-type="html"
        class="c-message-card__editor"
      )
      p.c-message-card__tip Tip: Include important details like what to bring, parking info, or contact information

  .c-message-card
    .c-message-card__header
      .c-message-card__badge 2
      .c-message-card__header-text
        h4.c-message-card__title Reminder Message
        p.c-message-card__subtitle This message will be sent to attendees 24 hours before the event

    .c-message-card__body
      .c-message-card__field-header
        fai(type="fal" icon="bell").c-message-card__icon
        span Automatic reminder 24 hours before event
      QuillEditor(
        :toolbar="['bold', 'italic', 'underline', 'strike']"
        :placeholder="'Hi! This is a friendly reminder that [Event Name] is tomorrow at [Time]. We can\\'t wait to see you! Don\\'t forget to...'"
        v-model:content="item.reminderEmail"
        content-type="html"
        class="c-message-card__editor"
      )
      p.c-message-card__tip Tip: Include last-minute details, directions, or what attendees should prepare

  .c-announcement-card(v-if="!isPublished || !stickerId")
    .c-announcement-card__icon-wrap
      fai(type="fal" icon="paper-plane").c-announcement-card__icon
    .c-announcement-card__text
      h4.c-announcement-card__title Announcement Feature
      p.c-announcement-card__desc Once you publish this event, you'll be able to send announcements and manage check-in from the host event tools (links appear here after publish).

  .c-host-tools(v-else)
    h4.c-host-tools__title Host event tools
    p.c-host-tools__desc These open in the Spotlight web app (same account). Use them to message ticket holders and check guests in at the door.
    .c-host-tools__links
      a.c-host-tools__link(
        :href="`/web/event/${stickerId}/announcements`"
        target="_blank"
        rel="noopener noreferrer"
      )
        fai(type="fal" icon="paper-plane").c-host-tools__link-icon
        span Send announcements
      a.c-host-tools__link(
        :href="`/web/event/${stickerId}/checkin`"
        target="_blank"
        rel="noopener noreferrer"
      )
        fai(type="fal" icon="check").c-host-tools__link-icon
        span Manage check-in
</template>

<style scoped lang="scss">
.c-tab-messages {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.c-message-card {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__badge {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f5a623;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Red Hat Display', sans-serif;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__header-text {
    flex: 1;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    color: #222;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-size: 13px;
    color: #888;
    margin: 0;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__body {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__field-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #555;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__icon {
    color: #888;
    font-size: 14px;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &__editor {
    border-radius: 6px;
    overflow: hidden;
  }

  &__tip {
    font-size: 12px;
    color: #aaa;
    margin: 0;
    font-family: 'Red Hat Display', sans-serif;
  }
}

.c-announcement-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
  background: #eef9ff;
  border: 1px solid #b3e0f5;
  border-radius: 10px;

  &__icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #d0edf9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__icon {
    font-size: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: #2a8ebf;
  }

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    color: #1a6080;
    margin: 0 0 4px 0;
  }

  &__desc {
    font-size: 13px;
    color: #4a8aaa;
    margin: 0;
    font-family: 'Red Hat Display', sans-serif;
    line-height: 1.5;
  }
}

.c-host-tools {
  padding: 16px 20px;
  background: #eef9ff;
  border: 1px solid #b3e0f5;
  border-radius: 10px;

  &__title {
    font-size: 14px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    color: #1a6080;
    margin: 0 0 6px 0;
  }

  &__desc {
    font-size: 13px;
    color: #4a8aaa;
    margin: 0 0 14px 0;
    font-family: 'Red Hat Display', sans-serif;
    line-height: 1.5;
  }

  &__links {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid #b3e0f5;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'Red Hat Display', sans-serif;
    color: #1a6080;
    text-decoration: none;
    width: fit-content;

    &:hover {
      background: #f5fcff;
      color: #124d66;
    }
  }

  &__link-icon {
    font-size: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}
</style>
